'use client';

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUpload } from '../contexts/UploadContext';
import { useSystemLoading } from '../contexts/LoadingContext';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { apiService } from '../services/apiService';
import {
  AIFraudOverviewResponse,
  AIFraudCasesResponse,
  DashboardOverviewResponse,
  SpecialtyAnalysis
} from '../types/dashboard';
import FraudOverview from './FraudOverview';
import FraudCases from './FraudCases';
import ClinicalOverview from './ClinicalOverview';
import Analytics from './Analytics';
import FileUpload from './FileUpload';
import ProviderChart from './ProviderChart';
import ProviderTableView from './ProviderTableView';
import DataQuality from './DataQuality';
import CaseServiceDistribution from './CaseServiceDistribution';
import ForecastingView from './ForecastingView';
import PerformanceIndicators from './PerformanceIndicators';
import BrandLogo from './BrandLogo';
import PhysicianReportsView, { PhysicianReportType } from './PhysicianReportsView';
import ProviderReportsView from './ProviderReportsView';
import { toTitleCase } from '../utils/formatters';

import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, BarChart, LineChart, LabelList
} from 'recharts';

import {
  LayoutDashboard,
  Stethoscope,
  ShieldAlert,
  Search,
  BarChart3,
  UploadCloud,
  CheckCircle2,
  TrendingUp,
  Gauge,
  FileText,
  Building2,
  ChevronRight,
  RefreshCw,
  LogOut,
  Users,
  Percent,
  Layers,
  Database,
  AlertTriangle,
  FileSpreadsheet,
  Activity,
  Cpu,
  FileBarChart2,
  DollarSign,
  Award,
  Scale
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
//  Types
// ─────────────────────────────────────────────────────────────────────────────
type TabType =
  | 'overview' | 'clinical' | 'fraud' | 'cases' | 'analytics' | 'upload'
  | 'data-quality' | 'providers-table' | 'providers-charts'
  | 'payers-table' | 'payers-charts' | 'postoffice-table' | 'postoffice-charts'
  | 'forecasting' | 'performance' | 'policy-benefits'
  | 'physician-benchmarking' | 'physician-fraud' | 'physician-inpatient-outpatient' | 'physician-change-amount' | 'physician-performance'
  | 'provider-reports-fraud' | 'provider-reports-services';

type SectionKey = 'providers' | 'payers' | 'postoffice';

interface OverviewChartsData {
  total_records: number;
  unique_members: number;
  unique_providers: number;
  unique_payers?: number;
  claims_volume_trend: { month: string; count: number }[];
  gross_amount_trend: { month: string; amount: number }[];
  gender_distribution: { name: string; value: number }[];
  age_group_distribution: { group: string; count: number }[];
  benefit_distribution: { benefit: string; count: number }[];
  service_type_distribution: { name: string; value: number }[];
  service_category_distribution: { category: string; count: number }[];
  fraud_trend?: { month: string; claims: number; fraudClaims: number; fraudRate: number }[];
  demographic_gender?: { group: string; male: number; female: number }[];
  service_category_trend?: { month: string; [key: string]: any }[];
  top_providers?: { name: string; amount: number }[];
  top_payers?: { name: string; amount: number }[];
  io_stats?: Record<string, { total: number; inpatient: number; outpatient: number }>;
}

const EMPTY_CHARTS: OverviewChartsData = {
  total_records: 0,
  unique_members: 0,
  unique_providers: 0,
  unique_payers: 0,
  claims_volume_trend: [],
  gross_amount_trend: [],
  gender_distribution: [],
  age_group_distribution: [],
  benefit_distribution: [],
  service_type_distribution: [],
  service_category_distribution: [],
  fraud_trend: [],
  demographic_gender: [],
  service_category_trend: [],
  top_providers: [],
  top_payers: [],
  io_stats: {
    "Full Year": { total: 0, inpatient: 0, outpatient: 0 },
    "YTD": { total: 0, inpatient: 0, outpatient: 0 },
    "Monthly": { total: 0, inpatient: 0, outpatient: 0 }
  }
};

// Accessible UI/UX Pro Max Chart Colors
const C = {
  blue: '#2563eb',
  indigo: '#4f46e5',
  pink: '#e11d48',
  amber: '#d97706',
  green: '#059669',
  red: '#dc2626',
  teal: '#0d9488',
};
const BAR_PALETTE = ['#2563eb', '#4f46e5', '#059669', '#d97706', '#0d9488', '#e11d48', '#7c3aed', '#0284c7', '#475569', '#ea580c'];

const fmtM = (v: number) => (v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : `$${(v / 1000).toFixed(0)}K`);

// ─────────────────────────────────────────────────────────────────────────────
//  Shared UI primitives
// ─────────────────────────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900/95 border border-slate-700/80 rounded-xl p-3 shadow-xl backdrop-blur-md text-xs text-white">
      <p className="font-semibold text-slate-400 mb-1.5">{label}</p>
      {payload.map((p: any, i: number) => (
        <div key={i} className="flex items-center justify-between gap-4 my-1">
          <span className="flex items-center gap-1.5 font-medium" style={{ color: p.color || '#fff' }}>
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color || '#fff' }} />
            {p.name}:
          </span>
          <span className="font-mono font-bold">{typeof p.value === 'number' ? p.value.toLocaleString() : p.value}</span>
        </div>
      ))}
    </div>
  );
};

const ChartCard = ({
  title,
  subtitle,
  children,
  className = '',
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={`glass-card rounded-2xl p-5 sm:p-6 transition-all duration-200 hover:shadow-card-hover ${className}`}>
    <div className="mb-4">
      <h3 className="text-sm font-bold text-slate-900 dark:text-white">{title}</h3>
      {subtitle && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const SectionDivider = ({ label }: { label: string }) => (
  <div className="flex items-center gap-3 my-6">
    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
    <span className="text-[11px] font-bold text-slate-400 tracking-wider">{label}</span>
    <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
  </div>
);

const IoStackBar = ({ label, total, inpatient, outpatient }: { label: string; total: number; inpatient: number; outpatient: number }) => (
  <div className="flex flex-col items-center gap-2 flex-1">
    <p className="text-sm font-extrabold text-slate-900 dark:text-white font-mono">{total.toLocaleString()}</p>
    <div className="w-11 h-28 rounded-lg overflow-hidden flex flex-col bg-slate-100 dark:bg-slate-800">
      <div style={{ height: `${outpatient}%`, backgroundColor: C.amber }} className="transition-all duration-500" />
      <div style={{ height: `${inpatient}%`, backgroundColor: C.indigo }} className="transition-all duration-500" />
    </div>
    <div className="text-center text-[10px] font-bold">
      <p style={{ color: C.amber }}>{outpatient}% Out</p>
      <p style={{ color: C.indigo }}>{inpatient}% In</p>
    </div>
    <p className="text-[11px] text-slate-500 font-semibold">{label}</p>
  </div>
);

const ComingSoon: React.FC<{ title: string; subtitle: string; icon: React.ComponentType<{ className?: string }> }> = ({
  title,
  subtitle,
  icon: Icon,
}) => (
  <div className="flex flex-col items-center justify-center p-12 sm:p-20 text-center">
    <div className="w-16 h-16 rounded-2xl bg-primary-50 dark:bg-primary-950/50 border border-primary-200 dark:border-primary-800 flex items-center justify-center text-primary-600 mb-4 shadow-sm">
      <Icon className="w-8 h-8" />
    </div>
    <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{title}</h3>
    <p className="text-xs text-slate-500 max-w-sm leading-relaxed mb-6">{subtitle}</p>
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-100/80 dark:bg-primary-950/60 border border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300 text-xs font-semibold">
      <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse" />
      <span>Module In Progress</span>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
//  Dashboard
// ─────────────────────────────────────────────────────────────────────────────
const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  const { data: policiesList } = useQuery({
    queryKey: ['policies-list'],
    queryFn: () => apiService.getPolicies(),
    staleTime: 5 * 60 * 1000,
  });

  const hasPolicies = policiesList && policiesList.length > 0;

  const [aiFraudOverview, setAIFraudOverview] = useState<AIFraudOverviewResponse | null>(null);
  const [aiFraudCases, setAIFraudCases] = useState<AIFraudCasesResponse | null>(null);
  const [dashboardOverview, setDashboardOverview] = useState<DashboardOverviewResponse | null>(null);
  const [specialties, setSpecialties] = useState<SpecialtyAnalysis[]>([]);
  const [overviewCharts, setOverviewCharts] = useState<OverviewChartsData>(EMPTY_CHARTS);
  const { setSystemLoading } = useSystemLoading();
  const { resetUpload } = useUpload();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>({
    providers: false,
    payers: false,
    postoffice: true,
  });
  const [isReportsOpen, setIsReportsOpen] = useState<boolean>(true);
  const [expandedReportCategories, setExpandedReportCategories] = useState<Record<string, boolean>>({
    physician: true,
    provider: false,
  });
  const [loadingProgress, setLoadingProgress] = useState(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toggleSection = (s: SectionKey) => setExpandedSections((p) => ({ ...p, [s]: !p[s] }));

  const loadDashboardData = useCallback(async (showFullScreen = false) => {
    try {
      if (showFullScreen) {
        setLoading(true);
        setSystemLoading(true, 'Aggregating Dashboard Metrics...');
        setLoadingProgress(0);
      }
      setError(null);

      const tasks = [
        { name: 'AI Fraud Overview', fn: () => apiService.getAIFraudOverview(0) },
        { name: 'Suspicious Cases', fn: () => apiService.getAIFraudCases(0.3, 10000) },
        { name: 'Dashboard Overview', fn: () => apiService.getDashboardOverview() },
        { name: 'Clinical Specialties', fn: () => apiService.getSpecialtiesData(50) },
        { name: 'Analytics Charts', fn: () => apiService.getDashboardOverviewCharts() },
      ];

      let completed = 0;
      const results = await Promise.all(
        tasks.map(async (task) => {
          try {
            const res = await task.fn();
            completed++;
            if (showFullScreen) setLoadingProgress(Math.floor((completed / tasks.length) * 100));
            return res;
          } catch (e) {
            completed++;
            if (showFullScreen) setLoadingProgress(Math.floor((completed / tasks.length) * 100));
            return null;
          }
        })
      );

      const [aiOverview, aiCases, overview, specialtiesData, chartsData] = results;

      if (aiOverview) setAIFraudOverview(aiOverview as AIFraudOverviewResponse);
      if (aiCases) setAIFraudCases(aiCases as AIFraudCasesResponse);
      if (overview) setDashboardOverview(overview as DashboardOverviewResponse);
      if (specialtiesData) setSpecialties(specialtiesData as SpecialtyAnalysis[]);
      if (chartsData) setOverviewCharts(chartsData as OverviewChartsData);
    } catch (e: any) {
      setError(e?.message || 'Failed to synchronize dashboard intelligence');
    } finally {
      if (showFullScreen) {
        setTimeout(() => {
          setLoading(false);
          setSystemLoading(false);
        }, 350);
      }
    }
  }, [setSystemLoading]);

  useEffect(() => {
    loadDashboardData(true);
    const currentTimeout = timeoutRef.current;
    return () => {
      if (currentTimeout) clearTimeout(currentTimeout);
    };
  }, [loadDashboardData]);

  const handleUploadComplete = useCallback((mode?: 'claims' | 'policies' | 'overview') => {
    if (mode === 'policies') {
      setActiveTab('policy-benefits');
    } else {
      setActiveTab('overview');
    }
    queryClient.invalidateQueries();
    loadDashboardData(false);
  }, [loadDashboardData, queryClient]);

  const getFraudValue = (k: string): any => (aiFraudOverview ? (aiFraudOverview as any)[k] : null);
  const highRiskCount = getFraudValue('fraud_claims') || 0;
  const fraudRate = getFraudValue('fraud_rate') || getFraudValue('fraudRate') || 0;
  const totalAnalyzed = getFraudValue('total_claims') || getFraudValue('totalClaims') || 0;
  const totalClaimsDisplay = dashboardOverview?.totalClaims || totalAnalyzed || 0;
  const totalAmountDisplay = dashboardOverview?.totalAmount || 0;
  const companyType = user?.company_type?.toLowerCase();
  const isProvider = companyType === 'provider';

  const totalRecords = totalClaimsDisplay || overviewCharts.total_records || 0;
  const totalUniqueMembers = overviewCharts.unique_members || 0;
  const totalUniqueProviders = overviewCharts.unique_providers || 0;
  const grossAmountTrend = overviewCharts.gross_amount_trend;
  const fraudTrend = overviewCharts.fraud_trend || [];
  const serviceStackData = overviewCharts.service_category_trend || [];
  const demographicData = overviewCharts.demographic_gender || [];

  const isPolicyBenefits =
    specialties?.some((s: any) => s.name === 'Policy Limit') ||
    dashboardOverview?.topSpecialty?.name === 'Policy Limit' ||
    (overviewCharts && overviewCharts.benefit_distribution?.some((b: any) => b.benefit === 'BENEFIT' || b.name === 'BENEFIT'));

  const mapCategoryName = (code: string) => {
    const map: Record<string, string> = {
      P: 'Pharmacy',
      I: 'Investigation',
      C: 'Consultation',
      T: 'Treatment/Procedure',
      X: 'Consumables',
    };
    return map[code.toUpperCase()] || code;
  };

  const topEntitiesData = (
    overviewCharts.top_payers && overviewCharts.top_payers.length > 0
      ? overviewCharts.top_payers
      : overviewCharts.top_providers || []
  ).slice(0, 3);
  const ioStats = overviewCharts.io_stats || EMPTY_CHARTS.io_stats;

  const TAB_OVERVIEW = { id: 'overview' as TabType, name: isPolicyBenefits ? 'Policy Overview' : 'Overview', badge: null, icon: LayoutDashboard };
  const TAB_POLICY_BENEFITS = { id: 'policy-benefits' as TabType, name: 'Policy Benefits', badge: null, icon: FileText };
  const TAB_CLINICAL = { id: 'clinical' as TabType, name: 'Clinical Analytics', badge: null, icon: Stethoscope };
  const TAB_FRAUD = { id: 'fraud' as TabType, name: 'Fraud Detection', badge: fraudRate > 0 ? `${Number(fraudRate).toFixed(1)}%` : null, icon: ShieldAlert };
  const TAB_CASES = { id: 'cases' as TabType, name: 'Suspicious Cases', badge: highRiskCount > 0 ? highRiskCount.toString() : null, icon: Search };
  const TAB_DATA_QUALITY = { id: 'data-quality' as TabType, name: 'Data Quality', badge: null, icon: CheckCircle2 };
  const TAB_FORECASTING = { id: 'forecasting' as TabType, name: 'Forecasting', badge: null, icon: TrendingUp };
  const TAB_PERFORMANCE = { id: 'performance' as TabType, name: 'Performance Indicators', badge: null, icon: Gauge };
  const TAB_UPLOAD = { id: 'upload' as TabType, name: isPolicyBenefits ? 'Upload Policies' : 'Upload Data', badge: null, icon: UploadCloud };

  const mainTabs = isPolicyBenefits
    ? [TAB_OVERVIEW, TAB_UPLOAD]
    : companyType === 'payer'
    ? [TAB_OVERVIEW, ...(hasPolicies ? [TAB_POLICY_BENEFITS] : []), TAB_DATA_QUALITY, TAB_PERFORMANCE, TAB_FORECASTING, TAB_UPLOAD]
    : [TAB_OVERVIEW, ...(hasPolicies ? [TAB_POLICY_BENEFITS] : []), TAB_CLINICAL, TAB_FRAUD, TAB_CASES, TAB_DATA_QUALITY, TAB_PERFORMANCE, TAB_FORECASTING, TAB_UPLOAD];

  const collapsibleSections = isPolicyBenefits
    ? []
    : companyType === 'payer'
    ? [
        {
          key: 'payers' as SectionKey,
          label: 'Provider Intelligence',
          color: '#2563eb',
          icon: Building2,
          subTabs: [{ id: 'payers-table' as TabType, label: 'Case & Service Distribution', icon: Layers }],
        },
        {
          key: 'postoffice' as SectionKey,
          label: 'Fraud Investigations',
          color: '#dc2626',
          icon: ShieldAlert,
          subTabs: [
            { id: 'payers-charts' as TabType, label: 'Overview', icon: BarChart3 },
            { id: 'postoffice-table' as TabType, label: 'Fraud by Providers', icon: Building2 },
            { id: 'postoffice-charts' as TabType, label: 'Physicians & Members', icon: Users },
          ],
        },
      ]
    : companyType === 'post-office'
    ? [
        {
          key: 'postoffice' as SectionKey,
          label: 'Post Office Operations',
          color: '#d97706',
          icon: Building2,
          subTabs: [
            { id: 'postoffice-table' as TabType, label: 'Table View', icon: FileSpreadsheet },
            { id: 'postoffice-charts' as TabType, label: 'Charts View', icon: BarChart3 },
          ],
        },
      ]
    : [];

  const reportCategories = [
    {
      id: 'physician',
      label: 'Physician Reports',
      icon: Stethoscope,
      badge: null,
      items: [
        { id: 'physician-benchmarking' as TabType, label: 'Peer Benchmarking', icon: Scale },
        { id: 'physician-fraud' as TabType, label: 'Fraud Detection', icon: ShieldAlert },
        { id: 'physician-inpatient-outpatient' as TabType, label: 'In/Out-Patient Analysis', icon: Activity },
        { id: 'physician-change-amount' as TabType, label: 'Change of Amount', icon: DollarSign },
        { id: 'physician-performance' as TabType, label: 'Physician Performance', icon: Award },
      ],
    },
    {
      id: 'provider',
      label: 'Provider Reports',
      icon: Building2,
      badge: 'Prepared',
      items: [
        { id: 'provider-reports-fraud' as TabType, label: 'Provider Fraud Overview', icon: ShieldAlert },
        { id: 'provider-reports-services' as TabType, label: 'Facility Service Analysis', icon: Layers },
      ],
    },
  ];

  const isPhysicianReportActive = [
    'physician-benchmarking',
    'physician-fraud',
    'physician-inpatient-outpatient',
    'physician-change-amount',
    'physician-performance',
  ].includes(activeTab);

  const isProviderReportActive = [
    'provider-reports-fraud',
    'provider-reports-services',
  ].includes(activeTab);

  const isReportsActive = isPhysicianReportActive || isProviderReportActive;

  useEffect(() => {
    if (isPhysicianReportActive) {
      setIsReportsOpen(true);
      setExpandedReportCategories((prev) => ({ ...prev, physician: true }));
    } else if (isProviderReportActive) {
      setIsReportsOpen(true);
      setExpandedReportCategories((prev) => ({ ...prev, provider: true }));
    }
  }, [isPhysicianReportActive, isProviderReportActive]);

  const toggleReports = () => {
    setIsReportsOpen((prev) => !prev);
  };

  const toggleReportCategory = (catId: string) => {
    setExpandedReportCategories((prev) => ({ ...prev, [catId]: !prev[catId] }));
  };

  // ── Loading screen ──────────────────────────────────────────────────────
  if (loading) {
    const STAGES = [
      { min: 0, max: 20, icon: Activity, label: 'Connecting to AI Fraud Engine...' },
      { min: 21, max: 40, icon: ShieldAlert, label: 'Scanning Suspicious Cases...' },
      { min: 41, max: 60, icon: BarChart3, label: 'Aggregating Dashboard Metrics...' },
      { min: 61, max: 80, icon: Stethoscope, label: 'Processing Clinical Data...' },
      { min: 81, max: 100, icon: Cpu, label: 'Finalizing Analytics Models...' },
    ];
    const activeStage = STAGES.find((s) => loadingProgress <= s.max) || STAGES[STAGES.length - 1];
    const ActiveStageIcon = activeStage.icon;

    return (
      <div className="min-h-screen w-full bg-slate-950 flex items-center justify-center p-4 fixed inset-0 z-50 selection:bg-primary-500">
        <div className="w-full max-w-md bg-slate-900/90 border border-slate-800 rounded-3xl p-8 backdrop-blur-xl shadow-2xl text-center space-y-6 animate-fade-in">
          <div className="relative mx-auto w-16 h-16 rounded-2xl bg-gradient-to-tr from-blue-600 to-primary-600 flex items-center justify-center text-white shadow-lg shadow-primary-500/30">
            <ShieldAlert className="w-8 h-8" />
          </div>

          <div>
            <h2 className="text-xl font-extrabold tracking-tight text-white">ClaimsAI Intelligence</h2>
            <p className="text-xs text-slate-400 mt-1">Aggregating multi-layer fraud and clinical analytics...</p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between text-xs font-mono text-slate-400">
              <span className="flex items-center gap-1.5 text-primary-400 font-semibold truncate">
                <ActiveStageIcon className="w-4 h-4 animate-spin shrink-0" />
                {activeStage.label}
              </span>
              <span className="text-white font-bold">{loadingProgress}%</span>
            </div>

            <div className="h-2 w-full bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 via-primary-500 to-emerald-400 rounded-full transition-all duration-300 shadow-[0_0_12px_rgba(37,99,235,0.4)]"
                style={{ width: `${loadingProgress}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Error screen ────────────────────────────────────────────────────────
  if (error)
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-6">
        <div className="glass-card rounded-2xl p-8 max-w-md w-full text-center space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-rose-100 dark:bg-rose-950/60 border border-rose-300 text-rose-600 mx-auto flex items-center justify-center">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Telemetry Synchronization Error</h3>
            <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">{error}</p>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => loadDashboardData(true)}
              className="flex-1 py-2.5 px-4 rounded-xl bg-primary-600 hover:bg-primary-500 text-white font-bold text-xs shadow-md transition-all cursor-pointer"
            >
              Retry Connection
            </button>
            <button
              onClick={() => window.location.reload()}
              className="flex-1 py-2.5 px-4 rounded-xl border border-slate-300 dark:border-slate-700 font-semibold text-xs text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer"
            >
              Reload
            </button>
          </div>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex">
      {/* ══ SIDEBAR ══ */}
      <aside className="w-64 shrink-0 bg-white/95 dark:bg-slate-900/95 border-r border-slate-200/80 dark:border-slate-800 flex flex-col h-screen sticky top-0 shadow-sm z-30 backdrop-blur-md">
        <div className="p-4 border-b border-slate-100 dark:border-slate-800">
          <BrandLogo size="md" />
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {/* Overview Tab (First Item) */}
          {mainTabs.slice(0, 1).map((tab) => {
            const isActive = activeTab === tab.id;
            const Icon = tab.icon;

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all duration-200 cursor-pointer ${
                  isActive
                    ? 'bg-primary-600 text-white shadow-md shadow-primary-600/25'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                <span className="flex-1 text-left truncate">{tab.name}</span>
              </button>
            );
          })}

          {/* ══ REPORTS MAIN NAVIGATION TAB ══ */}
          <div className="space-y-1">
            <button
              type="button"
              onClick={toggleReports}
              className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all duration-200 cursor-pointer ${
                isReportsActive
                  ? 'bg-primary-50 dark:bg-primary-950/60 text-primary-700 dark:text-primary-300 font-bold border border-primary-200/60 dark:border-primary-800/60 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <FileBarChart2 className={`w-4 h-4 shrink-0 ${isReportsActive ? 'text-primary-600 dark:text-primary-400' : 'text-slate-400'}`} />
                <span className="truncate">Reports</span>
              </div>
              <ChevronRight
                className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${
                  isReportsOpen ? 'rotate-90' : ''
                }`}
              />
            </button>

            {/* Nested Report Categories */}
            {isReportsOpen && (
              <div className="pl-3.5 ml-3 border-l-2 border-slate-200 dark:border-slate-800 space-y-1.5 my-1">
                {reportCategories.map((category) => {
                  const CategoryIcon = category.icon;
                  const isCatExpanded = expandedReportCategories[category.id];
                  const isThisCategoryActive = category.id === 'physician' ? isPhysicianReportActive : isProviderReportActive;

                  return (
                    <div key={category.id} className="space-y-1">
                      {/* Category Header */}
                      <button
                        type="button"
                        onClick={() => toggleReportCategory(category.id)}
                        className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer ${
                          isThisCategoryActive
                            ? 'text-primary-700 dark:text-primary-300 font-bold bg-primary-100/40 dark:bg-primary-900/30'
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <CategoryIcon className={`w-3.5 h-3.5 shrink-0 ${isThisCategoryActive ? 'text-primary-600' : 'text-slate-400'}`} />
                          <span className="truncate">{category.label}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {category.badge && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 font-medium">
                              {category.badge}
                            </span>
                          )}
                          <ChevronRight
                            className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${
                              isCatExpanded ? 'rotate-90' : ''
                            }`}
                          />
                        </div>
                      </button>

                      {/* Sub-Reports */}
                      {isCatExpanded && (
                        <div className="pl-3 ml-2.5 border-l-2 border-slate-200/80 dark:border-slate-800/80 space-y-1 my-0.5">
                          {category.items.map((sub) => {
                            const isSubActive = activeTab === sub.id;
                            const SubIcon = sub.icon;
                            return (
                              <button
                                key={sub.id}
                                onClick={() => setActiveTab(sub.id)}
                                className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs transition-all cursor-pointer ${
                                  isSubActive
                                    ? 'bg-primary-50 dark:bg-primary-950/60 text-primary-700 dark:text-primary-300 font-bold'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white font-medium hover:bg-slate-100/60 dark:hover:bg-slate-800/60'
                                }`}
                              >
                                <SubIcon className={`w-3.5 h-3.5 shrink-0 ${isSubActive ? 'text-primary-600' : 'text-slate-400'}`} />
                                <span className="truncate text-left">{sub.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Remaining Main Navigation Tabs */}
          {mainTabs.slice(1).map((tab) => {
            const isActive = activeTab === tab.id;
            const isUpload = tab.id === 'upload';
            const Icon = tab.icon;

            return (
              <button
                key={tab.id}
                onClick={() => {
                  if (tab.id === 'upload') {
                    resetUpload();
                  }
                  setActiveTab(tab.id);
                }}
                className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all duration-200 cursor-pointer ${
                  isUpload
                    ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-md shadow-emerald-600/25 hover:opacity-95'
                    : isActive
                    ? 'bg-primary-600 text-white shadow-md shadow-primary-600/25'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <Icon className={`w-4 h-4 shrink-0 ${isActive || isUpload ? 'text-white' : 'text-slate-400'}`} />
                <span className="flex-1 text-left truncate">{tab.name}</span>
                {tab.badge && (
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      isActive ? 'bg-white/20 text-white' : 'bg-rose-100 text-rose-700 dark:bg-rose-950/80 dark:text-rose-300'
                    }`}
                  >
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}

          {collapsibleSections.length > 0 && <div className="my-3 border-t border-slate-200/60 dark:border-slate-800" />}

          {collapsibleSections.map((section) => {
            const isExpanded = expandedSections[section.key];
            const SectionIcon = section.icon;

            return (
              <div key={section.key} className="space-y-1">
                <button
                  type="button"
                  onClick={() => toggleSection(section.key)}
                  className="w-full flex items-center justify-between px-3.5 py-2 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-2.5">
                    <SectionIcon className="w-4 h-4 text-primary-600" />
                    <span>{section.label}</span>
                  </div>
                  <ChevronRight
                    className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${
                      isExpanded ? 'rotate-90' : ''
                    }`}
                  />
                </button>

                {isExpanded && (
                  <div className="pl-4 ml-3 border-l-2 border-slate-200 dark:border-slate-800 space-y-1">
                    {section.subTabs.map((sub) => {
                      const isSubActive = activeTab === sub.id;
                      const SubIcon = sub.icon;
                      return (
                        <button
                          key={sub.id}
                          onClick={() => setActiveTab(sub.id)}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-all cursor-pointer ${
                            isSubActive
                              ? 'bg-primary-50 dark:bg-primary-950/60 text-primary-700 dark:text-primary-300 font-bold'
                              : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white font-medium'
                          }`}
                        >
                          <SubIcon className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">{sub.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-3 border-t border-slate-100 dark:border-slate-800 flex items-center gap-2">
          <button
            type="button"
            onClick={() => loadDashboardData(true)}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold transition-colors cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span>Sync Engine</span>
          </button>
          <button
            type="button"
            onClick={logout}
            title="Logout"
            className="p-2 rounded-xl bg-rose-50 dark:bg-rose-950/40 text-rose-600 hover:bg-rose-100 dark:hover:bg-rose-900/60 transition-colors cursor-pointer"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </aside>

      {/* ══ MAIN WORKSPACE ══ */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Sticky Top Header */}
        <header className="sticky top-0 z-20 glass-header px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="px-2.5 py-1 rounded-lg text-xs font-bold tracking-wide bg-primary-50 text-primary-700 border border-primary-200/60">
              {toTitleCase(companyType === 'post-office' ? 'Post Office Clearing' : `${companyType || 'Enterprise'} View`)}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2.5 px-3 py-1.5 rounded-xl bg-slate-100/80 dark:bg-slate-800/80 border border-slate-200/60 dark:border-slate-700/60">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-tr from-blue-600 to-primary-600 flex items-center justify-center text-white font-bold text-xs">
                {user?.name?.slice(0, 2).toUpperCase() || 'CO'}
              </div>
              <div className="text-left leading-tight hidden sm:block">
                <p className="text-xs font-bold text-slate-900 dark:text-white truncate">{user?.name || 'Compliance Officer'}</p>
                <p className="text-[10px] text-slate-500 truncate">{user?.email || 'compliance@system.com'}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Content Tabs Area */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          {activeTab === 'overview' &&
            (isPolicyBenefits ? (
              <CaseServiceDistribution />
            ) : (
              <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
                {/* Page Title */}
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white">
                      AI Fraud & Claims Intelligence Hub
                    </h1>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      Multi-layer statistical and anomaly screening across institutional claims
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <Activity className="w-3.5 h-3.5 animate-pulse" />
                      Live Feed
                    </span>
                  </div>
                </div>

                {/* KPI Bento Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="glass-card rounded-2xl p-5 border-l-4 border-l-blue-600">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[11px] font-bold text-slate-500 tracking-wide">Total Claims Evaluated</span>
                      <div className="p-2 rounded-xl bg-blue-50 text-blue-600">
                        <FileSpreadsheet className="w-4 h-4" />
                      </div>
                    </div>
                    <p className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white font-mono">
                      {totalClaimsDisplay.toLocaleString()}
                    </p>
                    <p className="text-xs font-semibold text-emerald-600 mt-2 flex items-center gap-1">
                      {totalAmountDisplay > 0 ? `USD ${totalAmountDisplay.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : 'Indexed Records'}
                    </p>
                  </div>

                  <div className="glass-card rounded-2xl p-5 border-l-4 border-l-rose-600">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[11px] font-bold text-slate-500 tracking-wide">High Risk Anomalies</span>
                      <div className="p-2 rounded-xl bg-rose-50 text-rose-600">
                        <ShieldAlert className="w-4 h-4" />
                      </div>
                    </div>
                    <p className="text-2xl sm:text-3xl font-extrabold text-rose-600 font-mono">
                      {highRiskCount.toLocaleString()}
                    </p>
                    <p className="text-xs font-semibold text-rose-500 mt-2">Requires Immediate Audit</p>
                  </div>

                  <div className="glass-card rounded-2xl p-5 border-l-4 border-l-emerald-600">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[11px] font-bold text-slate-500 tracking-wide">
                        {isProvider ? 'Unique Payers' : 'Unique Providers'}
                      </span>
                      <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600">
                        <Building2 className="w-4 h-4" />
                      </div>
                    </div>
                    <p className="text-2xl sm:text-3xl font-extrabold text-emerald-700 dark:text-emerald-400 font-mono">
                      {(isProvider ? overviewCharts.unique_payers || 0 : overviewCharts.unique_providers || 0).toLocaleString()}
                    </p>
                    <p className="text-xs font-semibold text-slate-500 mt-2">Network Facilities</p>
                  </div>

                  <div className="glass-card rounded-2xl p-5 border-l-4 border-l-amber-600">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[11px] font-bold text-slate-500 tracking-wide">Estimated Fraud Rate</span>
                      <div className="p-2 rounded-xl bg-amber-50 text-amber-600">
                        <Percent className="w-4 h-4" />
                      </div>
                    </div>
                    <p className="text-2xl sm:text-3xl font-extrabold text-amber-600 font-mono">
                      {Number(fraudRate).toFixed(1)}%
                    </p>
                    <p className="text-xs font-semibold text-slate-500 mt-2">AI Multi-Layer Confidence</p>
                  </div>
                </div>

                {/* Secondary Dataset Summary */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="glass-card rounded-2xl p-4 flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                      <Database className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-slate-500">Total Claim Lines</span>
                      <p className="text-lg font-bold font-mono text-slate-900 dark:text-white">
                        {totalRecords.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="glass-card rounded-2xl p-4 flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                      <Users className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-slate-500">Beneficiaries</span>
                      <p className="text-lg font-bold font-mono text-slate-900 dark:text-white">
                        {totalUniqueMembers.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="glass-card rounded-2xl p-4 flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
                      <Activity className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-slate-500">Treating Providers</span>
                      <p className="text-lg font-bold font-mono text-slate-900 dark:text-white">
                        {totalUniqueProviders.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Risk Distribution Bar */}
                <div className="glass-card rounded-2xl p-5">
                  <h4 className="text-xs font-bold text-slate-900 dark:text-white tracking-wide mb-3">
                    Risk Classification Breakdown
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <div className="flex justify-between text-xs font-semibold mb-1">
                        <span className="text-emerald-700 dark:text-emerald-400">Normal / Low Risk</span>
                        <span className="font-mono">
                          {totalAnalyzed > 0 ? (((totalAnalyzed - highRiskCount) / totalAnalyzed) * 100).toFixed(1) : 0}%
                        </span>
                      </div>
                      <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 rounded-full transition-all duration-700"
                          style={{
                            width: `${totalAnalyzed > 0 ? Math.max(0, ((totalAnalyzed - highRiskCount) / totalAnalyzed) * 100) : 0}%`,
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-xs font-semibold mb-1">
                        <span className="text-rose-700 dark:text-rose-400">Suspicious / High Risk</span>
                        <span className="font-mono">
                          {totalAnalyzed > 0 ? ((highRiskCount / totalAnalyzed) * 100).toFixed(1) : 0}%
                        </span>
                      </div>
                      <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-rose-500 rounded-full transition-all duration-700"
                          style={{
                            width: `${totalAnalyzed > 0 ? (highRiskCount / totalAnalyzed) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <SectionDivider label="Financial & Anomaly Analytics" />

                {/* Trend vs Fraud Chart */}
                {!isProvider && (
                  <ChartCard
                    title="Claims Volume Trend vs Fraud Detection Rate"
                    subtitle="Monthly total claims and detected fraud anomalies overlayed with fraud rate %"
                  >
                    <ResponsiveContainer width="100%" height={280}>
                      <ComposedChart data={fraudTrend} margin={{ top: 16, right: 30, left: -10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f080" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                        <YAxis
                          yAxisId="left"
                          tick={{ fontSize: 11, fill: '#64748b' }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          tick={{ fontSize: 11, fill: C.amber }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(v) => `${v}%`}
                          domain={[0, 100]}
                        />
                        <Tooltip content={<ChartTooltip />} />
                        <Legend iconType="circle" wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
                        <Bar yAxisId="left" dataKey="claims" name="Total Claims" fill={C.blue} radius={[4, 4, 0, 0]} maxBarSize={22} />
                        <Bar yAxisId="left" dataKey="fraudClaims" name="Flagged Fraud" fill={C.pink} radius={[4, 4, 0, 0]} maxBarSize={22} />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="fraudRate"
                          name="Fraud Rate %"
                          stroke={C.amber}
                          strokeWidth={2.5}
                          dot={{ fill: C.amber, r: 4 }}
                          activeDot={{ r: 6 }}
                        >
                          <LabelList dataKey="fraudRate" position="top" formatter={(v: number) => `${v}%`} style={{ fontSize: '10px', fill: C.amber, fontWeight: 700 }} />
                        </Line>
                      </ComposedChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}

                {/* Service Mix & In/Outpatient Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <ChartCard
                    title="Service Category Mix"
                    subtitle="Monthly breakdown across top clinical categories"
                    className="lg:col-span-2"
                  >
                    <ResponsiveContainer width="100%" height={230}>
                      <BarChart data={serviceStackData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f080" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                        <Tooltip content={<ChartTooltip />} />
                        <Legend iconType="circle" wrapperStyle={{ fontSize: '11px' }} />
                        {Object.keys(serviceStackData[0] || {})
                          .filter((k) => k !== 'month')
                          .map((cat, idx) => (
                            <Bar
                              key={cat}
                              dataKey={cat}
                              name={mapCategoryName(cat)}
                              stackId="a"
                              fill={BAR_PALETTE[idx % BAR_PALETTE.length]}
                            />
                          ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>

                  <ChartCard title="Service Type Share" subtitle="Inpatient vs Outpatient breakdown">
                    <div className="flex justify-around items-end pt-2">
                      {ioStats &&
                        Object.entries(ioStats).map(([label, stats]) => (
                          <IoStackBar key={label} label={label} total={stats.total} inpatient={stats.inpatient} outpatient={stats.outpatient} />
                        ))}
                    </div>
                    <div className="flex gap-4 justify-center mt-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: C.amber }} />
                        <span>Outpatient</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: C.indigo }} />
                        <span>Inpatient</span>
                      </div>
                    </div>
                  </ChartCard>
                </div>

                {/* Billed Amount & Top Entities */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {!isProvider && (
                    <ChartCard title="Gross Billed Amount Trend" subtitle="Monthly total financial volume in claims">
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={grossAmountTrend} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f080" />
                          <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={fmtM} />
                          <Tooltip content={<ChartTooltip />} />
                          <Line type="monotone" dataKey="amount" name="Gross Amount" stroke={C.indigo} strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </ChartCard>
                  )}

                  <ChartCard
                    title={isProvider ? 'Top 3 Payers by Billed Volume' : 'Top 3 Medical Providers by Billed Volume'}
                    subtitle="Ranked by total submitted financial claims"
                  >
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={topEntitiesData} layout="vertical" margin={{ top: 4, right: 48, left: 4, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f080" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={fmtM} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#475569' }} axisLine={false} tickLine={false} width={80} />
                        <Tooltip content={<ChartTooltip />} />
                        <Bar dataKey="amount" name="Amount" fill={C.green} radius={[0, 6, 6, 0]} maxBarSize={22}>
                          <LabelList dataKey="amount" position="right" formatter={fmtM} style={{ fontSize: '10px', fill: '#64748b', fontWeight: 600 }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </div>

                {/* Demographics */}
                <ChartCard title="Demographics — Age Segment by Gender" subtitle="Distribution of claims across age brackets and gender">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={demographicData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f080" vertical={false} />
                      <XAxis dataKey="group" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend iconType="circle" wrapperStyle={{ fontSize: '11px' }} />
                      <Bar dataKey="male" name="Male" fill={C.blue} radius={[4, 4, 0, 0]} maxBarSize={22} />
                      <Bar dataKey="female" name="Female" fill={C.pink} radius={[4, 4, 0, 0]} maxBarSize={22} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
            ))}

          {/* Other Tabs Rendering */}
          {activeTab !== 'overview' && (
            <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
              {activeTab === 'clinical' && (
                <div className="glass-card rounded-2xl overflow-hidden">
                  <div className="p-4 sm:p-6 border-b border-slate-200/80 dark:border-slate-800 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/20 dark:to-indigo-950/20 flex items-center gap-3">
                    <Stethoscope className="w-6 h-6 text-purple-600" />
                    <div>
                      <h2 className="text-base font-bold text-slate-900 dark:text-white">Clinical Specialties & Diagnoses</h2>
                      <p className="text-xs text-slate-500">Medical procedure patterns and clinical distribution</p>
                    </div>
                  </div>
                  <div className="p-4 sm:p-6">
                    <ClinicalOverview overview={dashboardOverview} specialties={specialties} />
                  </div>
                </div>
              )}

              {activeTab === 'fraud' && (
                <div className="glass-card rounded-2xl overflow-hidden">
                  <div className="p-4 sm:p-6 border-b border-slate-200/80 dark:border-slate-800 bg-gradient-to-r from-rose-50 to-amber-50 dark:from-rose-950/20 dark:to-amber-950/20 flex items-center gap-3">
                    <ShieldAlert className="w-6 h-6 text-rose-600" />
                    <div>
                      <h2 className="text-base font-bold text-slate-900 dark:text-white">AI Fraud Engine Intelligence</h2>
                      <p className="text-xs text-slate-500">Isolation Forest, statistical rules, and risk scoring</p>
                    </div>
                  </div>
                  <div className="p-4 sm:p-6">
                    <FraudOverview fraudOverview={aiFraudOverview} />
                  </div>
                </div>
              )}

              {activeTab === 'cases' && (
                <div className="glass-card rounded-2xl overflow-hidden">
                  <div className="p-4 sm:p-6 border-b border-slate-200/80 dark:border-slate-800 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950/20 dark:to-cyan-950/20 flex items-center gap-3">
                    <Search className="w-6 h-6 text-blue-600" />
                    <div>
                      <h2 className="text-base font-bold text-slate-900 dark:text-white">Suspicious Cases Audit</h2>
                      <p className="text-xs text-slate-500">High-priority claims flagged for compliance audit</p>
                    </div>
                  </div>
                  <div className="p-4 sm:p-6">
                    <FraudCases fraudCases={aiFraudCases} />
                  </div>
                </div>
              )}

              {activeTab === 'analytics' && (
                <div className="glass-card rounded-2xl overflow-hidden">
                  <div className="p-4 sm:p-6">
                    <Analytics />
                  </div>
                </div>
              )}

              {activeTab === 'data-quality' && (
                <div className="glass-card rounded-2xl p-4 sm:p-6">
                  <DataQuality />
                </div>
              )}

              {activeTab === 'performance' && (
                <div className="glass-card rounded-2xl p-4 sm:p-6">
                  <PerformanceIndicators />
                </div>
              )}

              {activeTab === 'forecasting' && (
                <div className="glass-card rounded-2xl p-4 sm:p-6">
                  <ForecastingView />
                </div>
              )}

              {activeTab === 'upload' && (
                <div className="glass-card rounded-2xl overflow-hidden p-2 sm:p-4">
                  <FileUpload onUploadComplete={handleUploadComplete} defaultMode={isPolicyBenefits ? 'policies' : 'claims'} />
                </div>
              )}

              {activeTab === 'policy-benefits' && (
                <div className="glass-card rounded-2xl overflow-hidden">
                  <div className="p-4 sm:p-6 border-b border-slate-200/80 dark:border-slate-800 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-950/20 dark:to-blue-950/20 flex items-center gap-3">
                    <FileText className="w-6 h-6 text-cyan-600" />
                    <div>
                      <h2 className="text-base font-bold text-slate-900 dark:text-white">Insurance Policy Terms & Limits</h2>
                      <p className="text-xs text-slate-500">OCR-extracted policy schedules, coverage rules, and caps</p>
                    </div>
                  </div>
                  <CaseServiceDistribution forcePolicyView={true} />
                </div>
              )}

              {activeTab === 'providers-charts' && (
                <div className="glass-card rounded-2xl overflow-hidden p-4 sm:p-6">
                  <ProviderChart />
                </div>
              )}

              {activeTab === 'providers-table' && (
                <div className="glass-card rounded-2xl overflow-hidden p-4 sm:p-6">
                  <ProviderTableView />
                </div>
              )}

              {activeTab === 'payers-table' && (
                <div className="glass-card rounded-2xl overflow-hidden">
                  <CaseServiceDistribution />
                </div>
              )}

              {activeTab === 'payers-charts' && (
                <div className="glass-card rounded-2xl overflow-hidden p-4 sm:p-6">
                  <FraudOverview fraudOverview={aiFraudOverview} />
                </div>
              )}

              {activeTab === 'postoffice-table' && (
                <div className="glass-card rounded-2xl overflow-hidden">
                  <ComingSoon
                    title="Provider Fraud Aggregations"
                    subtitle="Provider-level fraud profiling and comparative audit tables are being computed."
                    icon={Building2}
                  />
                </div>
              )}

              {activeTab === 'postoffice-charts' && (
                <div className="glass-card rounded-2xl overflow-hidden">
                  <ComingSoon
                    title="Physicians & Members Profiling"
                    subtitle="Member and physician anomaly correlation network graphs are generating."
                    icon={Users}
                  />
                </div>
              )}

              {/* Physician Reports Dedicated View */}
              {isPhysicianReportActive && (
                <PhysicianReportsView
                  activeReport={activeTab as PhysicianReportType}
                  setActiveReport={(rep) => setActiveTab(rep)}
                />
              )}

              {/* Provider Reports View */}
              {isProviderReportActive && (
                <ProviderReportsView
                  activeSubReport={activeTab}
                />
              )}
            </div>
          )}
        </main>
      </div>

      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
      )}

    </div>
  );
};

export default Dashboard;